// src/components/ProtectedRoute.jsx

import React, { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";


/* ============================================================
   LOGIN SESSION VALIDITY

   Example: login expires after 12 hours
============================================================ */

const SESSION_DURATION =
  12 * 60 * 60 * 1000;


/* ============================================================
   CLEAR LOGIN
============================================================ */

const clearLogin = () => {

  localStorage.removeItem("auth");
  localStorage.removeItem("user");
  localStorage.removeItem("userId");
  localStorage.removeItem("userName");
  localStorage.removeItem("username");
  localStorage.removeItem("userRole");
  localStorage.removeItem("userPhone");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("profilePicture");
  localStorage.removeItem("loginTime");

};


/* ============================================================
   CHECK LOGIN
============================================================ */

const checkAuth = () => {

  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) { return { authenticated: false, role: "" }; }

  const auth =
    localStorage.getItem("auth");

  const userId =
    localStorage.getItem("userId");

  const username =
    localStorage.getItem("username");

  const role = String(
    localStorage.getItem("userRole") || ""
  )
    .trim()
    .toLowerCase();

  const loginTime =
    Number(
      localStorage.getItem("loginTime") || 0
    );


  /* ==========================================================
     BASIC LOGIN CHECK
  ========================================================== */

  if (auth !== "loggedin") {

    return {
      authenticated: false,
      role: "",
    };

  }


  /* ==========================================================
     REQUIRED USER DETAILS CHECK
  ========================================================== */

  if (
    !userId ||
    !username ||
    !role
  ) {

    clearLogin();

    return {
      authenticated: false,
      role: "",
    };

  }


  /* ==========================================================
     VALID ROLES
  ========================================================== */

  const validRoles = [
    "doctor",
    "admin",
    "reception",
    "nursing station",
  ];


  if (!validRoles.includes(role)) {

    clearLogin();

    return {
      authenticated: false,
      role: "",
    };

  }


  /* ==========================================================
     LOGIN TIME MUST EXIST
  ========================================================== */

  if (!loginTime) {

    clearLogin();

    return {
      authenticated: false,
      role: "",
    };

  }


  /* ==========================================================
     SESSION EXPIRED
  ========================================================== */

  const now = Date.now();

  if (
    now - loginTime >
    SESSION_DURATION
  ) {

    clearLogin();

    return {
      authenticated: false,
      role: "",
    };

  }


  /* ==========================================================
     VALID LOGIN
  ========================================================== */

  return {
    authenticated: true,
    role,
  };

};



export default function ProtectedRoute({
  allowedRoles = [],
}) {

  const [authState, setAuthState] =
    useState(() => checkAuth());


  /* ============================================================
     REFRESH AUTH
  ============================================================ */

  useEffect(() => {

    const refreshAuth = () => {

      setAuthState(
        checkAuth()
      );

    };


    /* LOGIN / LOGOUT OTHER TAB */

    window.addEventListener(
      "storage",
      refreshAuth
    );


    /* CURRENT TAB LOGIN / LOGOUT */

    window.addEventListener(
      "authChanged",
      refreshAuth
    );


    /* USER RETURNS TO TAB */

    window.addEventListener(
      "focus",
      refreshAuth
    );


    /* CHECK SESSION EVERY 1 MINUTE */

    const timer =
      setInterval(
        refreshAuth,
        60 * 1000
      );


    return () => {

      window.removeEventListener(
        "storage",
        refreshAuth
      );

      window.removeEventListener(
        "authChanged",
        refreshAuth
      );

      window.removeEventListener(
        "focus",
        refreshAuth
      );

      clearInterval(timer);

    };

  }, []);



  /* ============================================================
     NOT LOGGED IN
  ============================================================ */

  if (!authState.authenticated) {

    return (
      <Navigate
        to="/login"
        replace
      />
    );

  }



  /* ============================================================
     CURRENT ROLE
  ============================================================ */

  const currentRole =
    authState.role;



  /* ============================================================
     DOCTOR / ADMIN FULL ACCESS
  ============================================================ */

  if (
    currentRole === "doctor" ||
    currentRole === "admin"
  ) {

    return <Outlet />;

  }



  /* ============================================================
     NO ROLE RESTRICTION
  ============================================================ */

  if (allowedRoles.length === 0) {

    return <Outlet />;

  }



  /* ============================================================
     NORMALIZE ALLOWED ROLES
  ============================================================ */

  const normalizedAllowedRoles =
    allowedRoles.map(
      (role) =>
        String(role)
          .trim()
          .toLowerCase()
    );



  /* ============================================================
     ROLE ALLOWED
  ============================================================ */

  if (
    normalizedAllowedRoles.includes(
      currentRole
    )
  ) {

    return <Outlet />;

  }



  /* ============================================================
     RECEPTION / NURSING REDIRECT
  ============================================================ */

  if (
    currentRole === "reception" ||
    currentRole === "nursing station"
  ) {

    return (
      <Navigate
        to="/frontdesk"
        replace
      />
    );

  }



  /* ============================================================
     INVALID LOGIN
  ============================================================ */

  clearLogin();

  return (
    <Navigate
      to="/login"
      replace
    />
  );

}